<template>
  <w-autocomplete
    v-bind="$props"
    ref="autocomplete"
    :items="sortedLazyItems"
    :search-input.sync="displayedString"
    :display-string="displayedString"
    :cache-items="cacheItems"
    hide-selected
    v-on="$listeners"
    @focus="handleFocus"
    @click:clear="handleClickClear"
    @change="handleChange"
  >
    <template #prepend-item>
      <v-list-item
        v-show="!isPending && noDataIsVisible"
        class="v-list-item__no-data"
      >
        <div class="v-list-item__title">
          {{ $t('Common.noData') }}
        </div>
      </v-list-item>
    </template>

    <template #append>
      <slot name="append-inner" />
    </template>

    <template #append-item>
      <v-list-item
        v-if="!noNewResults"
        @click="invokeLoadNextData"
      >
        <div class="w-autocomplete-lazy__load-more">
          {{ $t('Common.loadMore') }}
        </div>
      </v-list-item>
    </template>

    <template 
      v-for="(index, name) in $scopedSlots" 
      #[name]="data"
    >
      <slot 
        :name="name" 
        v-bind="data" 
      />
    </template>
  </w-autocomplete>
</template>


<script>
// components
import WorkAutocomplete from '../global_components/forms/WorkAutocomplete';

// helpers
import axios from 'axios';
import { mergeData } from '@/utils/merge-data-util';
import get from 'lodash/get';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';

// mixins
import lazyLoading from '../../mixins/lazy-loading-mixin';
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';
import sortedItems from '@/mixins/component/sorted-items-mixin';
import { sortItemsModule } from '@/mixins/component/sorted-items-mixin';

export default {
  name: 'WorkAutocompleteLazy',
  props: {
    ...WorkAutocomplete.props,
    cacheItems: { type: Boolean, default: true },
    path: {
      type: String,
      required: true,
    },
    params: Object,
    getData: {
      type: Function,
      default: (response) => response.data.data?.map((item) => item.data),
    },
    getReloadPath: { type: Function, default: undefined },
    getDisplayedString: {
      type: Function,
      default: function (response, textPath) {
        let usedTextPath = textPath ?? this.itemText;
        if (isFunction(usedTextPath)) {
          let displayedString = usedTextPath(response?.data);
          if (!displayedString || displayedString.includes('undefined'))
            return usedTextPath(response);
          return displayedString;
        }
        return (
          get(response, usedTextPath) ??
          get(response?.data, usedTextPath) ??
          get(response?.data?.data, usedTextPath)
        );
      },
    },
    restoreInitialValues: { type: Boolean, default: false },
    apiGetResponseItemText: undefined,
    excludeIds: { type: Array, required: false },
    mandatoryFilters: {
      type: Object,
      default: function () {
        return {};
      },
    },
    ignoreReloadData: { type: Boolean, default: false },
    forceToReloadData: { type: Boolean, default: false },
    emptyDisplayString: { type: String },
    customReloadPath: { type: Function },
    sortDesc: { type: Array, default: () => [] },
    sortBy: { type: Array },
    sortItems: { type: Boolean, default: false },
    noSearchBySortParam: { type: Boolean, default: false },
  },
  data() {
    return {
      displayedString: undefined,
      forceReload: false,
      noDataIsVisible: false,
      wasActivatedByUser: false,
    };
  },
  methods: {
    invokedParamHandler(params) {
      let parsedParams = params;

      if(!this.noSearchBySortParam 
        && params?.sort !== null 
        && params?.sort !== undefined 
        && params?.sort !== '' 
        && params?.sort.length === 1
        && params?.search !== undefined 
        && params?.search !== null 
        && params?.search !== ''
        && !params?.search.includes(':')
      ) {
        let sortParams = params.sort.includes(':') ? params.sort.split(':') : params.sort;
        let sortBy = sortParams?.length > 1 ? sortParams[0] : params.sort;
        let searchBy = params.search.trim().replace(/\s+/g, '*');

        parsedParams.search = `${sortBy}:*${searchBy}*`;
      }

      return { parsedParams: parsedParams };
    },
    escapeString(string) {
      return string?.replaceAll(/([:()"\[\]{}~?\\])/gi, "\\$1");
    },
    handleChange() {
      if (this.multiple) {
        this.displayedString = undefined;
      }
    },
    handleClickClear() {
      this.displayedString = undefined;
      this.setSearch(undefined);
      this.forceReload = true;
      this.loadInitialItems();
    },
    handleFocus() {
      this.wasActivatedByUser = true;
      this.loadInitialItems();
    },
    handleAfterReload() {
      if (!this._isMultipleSelection(this.value)) this._setCachedItems();
      this.noDataIsVisible = this.showNoData();
    },
    setSearch(search) {
      if (this.$refs?.autocomplete === undefined) {
        this.displayedString = search;
      } else if (this.$refs?.autocomplete?.$refs?.vAutocomplete === undefined) {
        this.displayedString = search;
      } else {
        this.$refs.autocomplete.$refs.vAutocomplete.setSearch(search);
      }
    },
    async loadInitialItems() {
      if (
        isEmpty(this.lazyItems) ||
        this.forceReload === true ||
        this.isFocused === true
      ) {
        this.$refs.autocomplete.cachedItems = [];
        await this.reload();
        this.handleAfterReload();
      } else this.noDataIsVisible = this.showNoData();
    },
    _isMultipleSelection(value) {
      return this.multiple === true && Array.isArray(value);
    },
    _isObject(value) {
      return typeof value === 'object';
    },
    focus() {
      this.$refs.autocomplete.focus();
    },
    _addMissingLazyItem(value) {
      let index = null;
      if (isFunction(this.itemValue)) {
        index = this.lazyItems.findIndex(
          (item) => this.itemValue(item) == this.itemValue(value)
        );
      } else {
        index = this.lazyItems.findIndex(
          (item) => get(item, this.itemValue) == get(value, this.itemValue)
        );
      }
      if (index < 0) {
        this.lazyItems = [...this.lazyItems, value];
      }
      this._setCachedItems();
    },
    async _setCachedItems() {
      await this.$nextTick();
      if (this.$refs?.autocomplete != undefined) {
        this.$refs.autocomplete.cachedItems = this.lazyItems;
        this.noDataIsVisible = this.showNoData();
      }
    },
    async invokeLoadNextData() {
      await this.loadNextData();
    },
    getDisplayedStringByObject(response) {
      return this.apiGetResponseItemText
        ? this.getDisplayedString(response, this.apiGetResponseItemText)
        : this.getDisplayedString(response);
    },
    hasDisplayedString(displayString) {
      if (displayString?.includes('undefined')) return false;
      if (isFunction(this.itemText) && this.emptyDisplayString && displayString == this.emptyDisplayString) return false
      return displayString != undefined && displayString != null;
    },
    showNoData() {
      const cachedLength = this.$refs?.autocomplete?.cachedItems?.length ?? 0;
      return cachedLength === 0 && !this.isPending && !this.isLoading;
    },
    reset() {
      this.$refs.autocomplete.reset();
    }
  },
  mixins: [
    isRequired,
    pendingAble,
    sortedItems,
    lazyLoading(
      (self) => self.path,
      (self) =>
        mergeData(
          {},
          {
            ...self.params,
            excludeIds: self.excludeIds,
            search: self.escapeString(self.displayedString) ?? undefined,
            mandatoryFilters: self.mandatoryFilters,
            sort: self.sortBy  ?? self.sortedLabel,
            sortDesc: self.sortDesc,
          }
        ),
      50,
      (self) => self.getData,
      (self) => self.invokedParamHandler,
      undefined,
      undefined,
      (self) => self.restoreInitialValues,
      (self) => self.handleAfterReload,
      self => !self.wasActivatedByUser
    ),
  ],
  computed: {
    reloadPathWithDefault() {
      if (this.customReloadPath !== undefined) { 
        return this.customReloadPath(this.value)
      }

      let internalValue = `?ids=${this.value}`;
      let reloadPath = undefined;

      if (this._isObject(this.value)) {
        internalValue = `?ids=${get(this.value, this.itemValue)}`;
      }

      if (this._isMultipleSelection(this.value)) {
        let ids = this.value.map((item) => get(item, this.itemValue) ?? item);
        internalValue = `?ids=${ids.join(',')}&size=${this.value.length}`;
      }

      if (internalValue.endsWith('undefined')) {
        return undefined;
      }

      if (
        this.getReloadPath !== undefined &&
        !this._isMultipleSelection(this.value)
      ) {
        reloadPath = this.getReloadPath(
          this._isObject(this.value)
            ? get(this.value, this.itemValue)
            : this.value
        );
      }
      return `/rest/v4/${reloadPath ?? this.path + internalValue}`;
    },
    singleItemReloadPath() {
      if (!this._isObject(this.value) || this.value?.length > 1) {
        return undefined;
      }
      let internalValue = this.value?.length == 1 ? `/${get(this.value[0], this.itemValue) ?? this.value[0]}` : `/${get(this.value, this.itemValue)}`;
      if (internalValue.endsWith('undefined')) {
        return undefined;
      }
      return `/rest/v4/${this.path + internalValue}`;
    },
    sortedLazyItems() {
      return sortItemsModule.getSortedItems(this.$props, this.lazyItems);
    },
    sortedLabel() {
      return isFunction(this.itemText)
        ? []
        : [this.itemText.split('.')?.pop()];
    },
    isFocused() {
      return this.$refs?.autocomplete?.isFocused;
    }
  },
  watch: {
    value: {
      async handler(val) {
        if (
          !val ||
          (this._isMultipleSelection(val) && val.length === 0) ||
          (this._isObject(val) && isEmpty(val)) ||
          this.ignoreReloadData === true
        ) {
          return;
        }

        if (!this._isMultipleSelection(val) && !this.restoreInitialValues) {
          let valueObj = this._isObject(val)
            ? val
            : this.$refs.autocomplete?.cachedItems?.find(
                (item) => get(item, this.itemValue) == val
              );
          let displayedStringTemp = this.getDisplayedStringByObject(valueObj);
          let hasDisplayedTempString =
            this.hasDisplayedString(displayedStringTemp);
          if (hasDisplayedTempString) this.setSearch(displayedStringTemp);

          if (
            (hasDisplayedTempString === false && val) ||
            this.forceToReloadData === true
          ) {
            this.wasActivatedByUser = true;
            const reloadPath = this.reloadPathWithDefault;

            if (reloadPath) {
              axios.get(reloadPath).then((response) => {
                let responseData = undefined;

                if (this.getReloadPath === undefined && response.data?.data?.length > 0) {
                  responseData = this.getData(response)[0];
                }
                if (responseData == undefined) {
                  responseData = response['id'] != undefined
                    ? response
                    : response.data;
                }
                
                if (Array.isArray(responseData)) { // if more than one matching response, filter by value
                  responseData = responseData?.find(item => item.id == val)
                }

                let displayedStringByObj =
                  this.getDisplayedStringByObject(responseData);
                this.setSearch(displayedStringByObj);
                if (displayedStringByObj != undefined) {
                  if (isFunction(this.itemText)) {
                    // ignore
                  } else {
                    set(responseData, this.itemText, displayedStringByObj);
                  }

                  this._addMissingLazyItem(responseData);
                }
              });
            }
          } else if (
            val &&
            (get(val, this.itemValue) || isFunction(this.itemValue))
          ) {
            this._addMissingLazyItem(val);
          }
        } else {
          if (this.restoreInitialValues) {
            if (Array.isArray(val)) {
              for (var i = 0; i < val.length; i++) {
                if (
                  !this.selectedItems.some(
                    (item) =>
                      get(item, this.itemValue) == get(val[i], this.itemValue)
                  )
                ) {
                  this.selectedItems = [...this.selectedItems, val[i]];
                }

                if (
                  !this.lazyItems.some(
                    (item) =>
                      get(item, this.itemValue) == get(val[i], this.itemValue)
                  )
                ) {
                  this.lazyItems = [...this.lazyItems, val[i]];
                  this._setCachedItems();
                }
              }
            } else {
              if (
                !this.lazyItems.some(
                  (item) =>
                    get(item, this.itemValue) == get(val, this.itemValue)
                )
              ) {
                this.lazyItems = [...this.lazyItems, val];
                this._setCachedItems();
              }
            }
          } else if (
            !val.every((item) =>
              this.lazyItems.includes(
                (lazy) =>
                  (get(lazy, this.itemValue) ?? lazy) ==
                  (get(item, this.itemValue) ?? item)
              )
            )
          ) {
            let response = await axios.get(this.reloadPathWithDefault);
            
            if (this._isMultipleSelection(response.data.data) && response.data.data?.length > 0) {
              let loadedData = this.getData(response);
              for (var i = 0; i < loadedData.length; i++) {
                this._addMissingLazyItem(loadedData[i]);
              }
              this.forceReload = true;
            }
            else {
              response = await axios.get(this.singleItemReloadPath)
              if (response?.data) {
                this._addMissingLazyItem(response.data);
              }
            }
          }
        }
      },
      immediate: true,
    },
    excludeIds() {
      this.forceReload = true;
    },
    path(newVal, oldVal) {
      if (newVal != oldVal && oldVal) {
        this.forceReload = true;
        this.loadInitialItems();
      }
    },
    ['$refs.autocomplete.cachedItems']() {
      this.noDataIsVisible = this.showNoData();
    },
    lazyItems() {
      if (this.lazyItems.length > this.$refs?.autocomplete?.lastItem)
        this.$refs.autocomplete.lastItem = this.lazyItems.length;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .w-autocomplete-lazy__load-more {
  color: $button-color;
}
::v-deep .v-list-item[tabindex^='-']:not(.v-list-item__no-data) {
  display: none;
}
</style>
